/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { CircularProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMusic, FiPause } from "react-icons/fi";
import { useSearchParams } from "react-router";
import { GroupedVirtuoso, VirtuosoHandle } from "react-virtuoso";
import { useIsMobile } from "~/hook/use-mobile";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { KirtanLyricsdata } from "~/interface/kirtan-lyrics/kirtan-lyrics";
import { setSession } from "~/lib/helperFuncation/sessionStorageService";
import { addToCurrentAudio, changeIsAudioPlayState, changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";
import { COLORS, LANG, SELECTED_PAD } from "~/utils/constant";
import ClientOnly from "../shared-components/ClentOnly";
import NodataFound from "../shared-components/NoDataFound";
import NoHasMoreText from "../shared-components/NoHasMoreText";
import Tag from "../shared-components/Tag";

interface KirtanLyricsDataProps {
  kirtanId?: number | null;
  selectedKirtanDetail?: KirtanLyricsdata | null;
  selectedKirtanId?: number | null;
  kirtanLyricsData: KirtanLyricsdata[];
  rechaytaData: MstPersonType[];
}

const KirtanLyricsData = ({ selectedKirtanDetail, selectedKirtanId, kirtanLyricsData, rechaytaData }: KirtanLyricsDataProps) => {
  const lang = useLanguageParams();
  const { t } = useTranslation();
  const { semiBoldInter } = useFontStyle();
  const [searchParams] = useSearchParams();
  const { kirtanCharacters, rachayta, hoveredChar } = useAppSelector((state) => state.kirtanLyrics);
  const isMobile = useIsMobile();

  const virtuoso = useRef<VirtuosoHandle | null>(null);
  const urlChar = searchParams.get("char");
  const [skeletonLoader, setSkeletonLoader] = useState(true);
  const prevLangRef = useRef(lang);
  const [lastSelectedKirtanId, setLastSelectedKirtanId] = useState<number | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  const { groups, groupCounts } = useMemo(() => {
    const filtered = kirtanCharacters
      .map((char) => {
        const count = kirtanLyricsData.filter((kirtan) => (lang === LANG.gujarati ? kirtan.gc === char : kirtan.ec === char)).length;
        return { char, count };
      })
      .filter((item) => item.count > 0);

    return {
      groups: filtered.map((item) => item.char),
      groupCounts: filtered.map((item) => item.count),
    };
  }, [kirtanCharacters, kirtanLyricsData, lang]);

  const groupedData = useMemo(() => {
    const result: typeof kirtanLyricsData = [];
    kirtanCharacters.forEach((char) => {
      const items = (kirtanLyricsData ?? [])
        .filter((kirtan) => (lang === LANG.gujarati ? kirtan.gc === char : kirtan.ec === char))
        .sort((a, b) => (lang === LANG.gujarati ? (a.gt ?? "").localeCompare(b.gt ?? "") : (a.et ?? "").localeCompare(b.et ?? "")));
      result.push(...items);
    });
    return result;
  }, [kirtanLyricsData, kirtanCharacters, lang]);

  useEffect(() => {
    const groupIndex = groups.findIndex((c) => c === hoveredChar);
    if (groupIndex !== -1 && virtuoso.current) {
      virtuoso.current.scrollToIndex({
        index: groupCounts.slice(0, groupIndex).reduce((a, b) => a + b, 0),
        ...(isMobile && {
          align: "start",
          offset: -80,
          behavior: "auto",
        }),
      });
    }
  }, [hoveredChar, groups, groupCounts]);

  // Auto-scroll to selected kirtan item
  useEffect(() => {
    if (!virtuoso.current || isScrollingRef.current) return;

    if (selectedKirtanId) {
      const selectedIndex = groupedData.findIndex((kirtan) => kirtan.i === selectedKirtanId);
      if (selectedIndex !== -1) {
        isScrollingRef.current = true;
        virtuoso.current.scrollToIndex({
          index: selectedIndex,
          align: "center",
          ...(isMobile && { offset: -80 }),
        });
        // Reset flag after scroll completes
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          isScrollingRef.current = false;
        }, 300);
      }
    } else if (!selectedKirtanId && lastSelectedKirtanId) {
      // When panel closes, scroll to last selected item
      const lastIndex = groupedData.findIndex((kirtan) => kirtan.i === lastSelectedKirtanId);
      if (lastIndex !== -1) {
        isScrollingRef.current = true;
        virtuoso.current.scrollToIndex({
          index: lastIndex,
          align: "center",
          ...(isMobile && { offset: -80 }),
        });
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          isScrollingRef.current = false;
        }, 300);
      }
    }
  }, [selectedKirtanId, groupedData, lastSelectedKirtanId, isMobile]);

  // Show loading state when language changes
  useEffect(() => {
    if (prevLangRef.current !== lang) {
      setSkeletonLoader(true);
      prevLangRef.current = lang;
    }
  }, [lang]);

  // Track last selected kirtan for visual indication
  useEffect(() => {
    if (selectedKirtanId) {
      setLastSelectedKirtanId(selectedKirtanId);
    }
  }, [selectedKirtanId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    // Use a small timeout to allow the UI to render the skeleton first
    const timeout = setTimeout(() => {
      if (kirtanLyricsData.length > 0) {
        setSkeletonLoader(false); // If data is already present, stop loading immediately
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [kirtanLyricsData, lang]);

  return (
    <div className="w-full h-full flex justify-start items-start gap-1.5 md:gap-4 relative">
      <div className="w-full h-full flex flex-col justify-start items-start gap-4">
        <ClientOnly
          fallback={
            <div className="w-full h-screen flex flex-col justify-start items-start overflow-y-auto scrollbar-visible custom-scrollbar">
              {/* Sticky Header with Character */}
              <div className="w-full sticky top-0 z-10 bg-secondaryBackground flex justify-start items-center py-2 border-b-[1px] border-borderPrimary px-2">
                <span className={cn("body_text_l text-textColor min-w-0 max-w-0 capitalize", semiBoldInter)}>{urlChar || kirtanCharacters[0]}</span>
              </div>
              {/* Scrollable Content */}
              <div className="w-full flex flex-col justify-start items-start">
                {kirtanLyricsData.map((kirtan) => {
                  const rachayta_data = rechaytaData || rachayta || [];
                  const rachayta_name = rachayta_data.find((item) => item.id === kirtan?.r)?.text || null;

                  return (
                    <div className="w-full flex flex-col justify-start items-start" key={kirtan?.i}>
                      <KirtanDataRow kirtan={kirtan} rachayta={rachayta_name || null} />
                    </div>
                  );
                })}
              </div>
            </div>
          }
        >
          {kirtanLyricsData?.length > 0 ? (
            <GroupedVirtuoso
              ref={virtuoso}
              style={{
                height: "100%",
                width: "100%",
              }}
              groupCounts={groupCounts}
              // useWindowScroll={isDesktop}

              useWindowScroll={isMobile}
              groupContent={(index) => {
                return (
                  <div className="w-full flex justify-start bg-secondaryBackground items-center py-2 border-b-[1px] border-borderPrimary px-2">
                    <span className={cn("body_text_l text-textColor min-w-0 max-w-0 capitalize", semiBoldInter)}>{groups[index]}</span>
                  </div>
                );
              }}
              itemContent={(index) => {
                const kirtan = groupedData[index];
                const rachayta_data = rechaytaData || rachayta || [];
                const rachayta_name = rachayta_data.find((item) => item.id === kirtan?.r)?.text || null;
                const isSelected = selectedKirtanId
                  ? selectedKirtanId === kirtan?.i
                  : isMobile
                    ? lastSelectedKirtanId === kirtan?.i
                    : selectedKirtanDetail?.i === kirtan?.i;
                return (
                  <div className="w-full flex flex-col justify-start items-start">
                    <KirtanDataRow kirtan={kirtan} rachayta={rachayta_name} isSelected={isSelected} />
                  </div>
                );
              }}
              components={{
                Footer: () => (
                  <>
                    {skeletonLoader ? (
                      <div className="w-full h-dvh flex flex-col justify-center items-center space-y-2">
                        <SkeletonLoader />
                      </div>
                    ) : (
                      <NoHasMoreText
                        className="text-textSecondaryColor mb-5"
                        text={t("common.seeAllData", { data: lang === LANG.english ? "Kirtans" : "કીર્તન" })}
                      />
                    )}
                  </>
                ),
              }}
              className={isMobile ? "scrollbar-hide" : "custom-scrollbar"}
            />
          ) : (
            <>
              <div className="h-[60vh] w-full flex justify-center items-center px-4">
                <div className="flex flex-col justify-center items-center gap-4">
                  <div className="size-40 rounded-full overflow-hidden flex justify-center items-center bg-gray-200 flex-shrink-0">
                    <img src="/images/noDataMaharaj.png" alt="maharaj_image" className="rounded-full object-cover select-none" />
                  </div>
                  <p className={cn("h2_heading font-bold text-textColor text-center max-w-xs break-words", semiBoldInter)}>
                    {t("common.noDataFoundFor", { data: lang === LANG.english ? "Kirtans" : "કીર્તન" })}
                  </p>
                </div>
              </div>
            </>
          )}
        </ClientOnly>
      </div>
    </div>
  );
};

const KirtanDataRow = ({
  kirtan,
  rachayta,
  isLoading = false,
  isSelected = false,
}: {
  kirtan: KirtanLyricsdata;
  rachayta: string | null;
  isLoading?: boolean;
  isSelected?: boolean;
}) => {
  const lang = useLanguageParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const { currentAudio, isPlaying } = useAppSelector((state) => state.nityNiyam);
  const { mediumInter, normalInter } = useFontStyle();

  // if (isLoading) {
  //   return <SkeletonLoader />;
  // }

  const handleOnAudioCLick = (kirtan: KirtanLyricsdata) => {
    if (kirtan?.tr) {
      dispatch(changeIsAudioPlayState(true));
      dispatch(changeIsPlayingState(true));
      dispatch(
        addToCurrentAudio({
          id: kirtan?.i,
          img: null,
          totalDuration: null,
          artist: rachayta,
          name: lang === LANG.gujarati ? kirtan?.gt : kirtan?.et,
          audio: kirtan?.tr,
          from: "kirtan-lyrics",
        }),
      );
    }
  };

  const handleNavigate = useCallback(
    (kirtanId: number) => {
      // Preserve existing filter params and add kirtanId
      const newParams = new URLSearchParams(searchParams);
      newParams.set("kirtanId", String(kirtanId));
      setSearchParams(newParams);

      // Dispatch event to trigger both pad data fetch and React sync
      window.dispatchEvent(new CustomEvent("kirtanIdChanged", { detail: { kirtanId } }));

      setSession(SELECTED_PAD, kirtan?.n === null ? "1" : kirtanId.toString());
    },
    [kirtan?.n, searchParams.toString()],
  );

  return (
    <div
      className={cn(
        "w-full p-2 flex justify-between items-center cursor-pointer border-b-[1px] border-borderPrimary transition-all duration-100 ease-in-out select-none",
        isSelected ? "bg-darkOrangeColor bg-opacity-20 border-l-4 border-l-darkOrangeColor" : "bg-primary hover:bg-cardBg",
      )}
      key={kirtan?.i}
      onClick={() => handleNavigate(kirtan.i)}
    >
      <div className="flex flex-col justify-start items-start">
        <div className="w-full flex justify-start items-center gap-2 pr-3">
          <span className={cn("body_text_m text-textColor line-clamp-1 capitalize", mediumInter)}>
            {lang === LANG.gujarati ? kirtan?.gt : kirtan?.et || kirtan?.et}
          </span>
          {kirtan?.tg && <Tag text={kirtan?.tg} />}
        </div>
        {kirtan?.r && <span className={cn("body_text_s text-textSecondaryColor", normalInter)}>{rachayta}</span>}
      </div>
      <div className="flex justify-center items-center gap-1">
        {/* video button */}
        {/* {kirtan.track_path && (
        <div className="p-2 rounded-full border-[1px] border-borderPrimary flex justify-center items-center">
          <PiPlay size={15} className="text-textHeadingColor" />
        </div>
      )} */}

        {/* audio button */}
        {kirtan?.tr &&
          (currentAudio !== null && currentAudio.from === "kirtan-lyrics" && currentAudio.id === kirtan?.i && isPlaying ? (
            // Audio is playing — show Pause icon
            <div
              className="p-1 rounded-full border-[1px] border-borderPrimary flex justify-center items-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                dispatch(changeIsPlayingState(false));
              }}
            >
              <FiPause size={12} className="text-textHeadingColor" />
            </div>
          ) : (
            // Audio is not playing — show Music (Play) icon
            <div
              className="p-1 rounded-full border-[1px] border-borderPrimary flex justify-center items-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleOnAudioCLick(kirtan);
              }}
            >
              <FiMusic size={12} className="text-textHeadingColor" />
            </div>
          ))}

        {/* total pad */}
        <div className="w-[45px] mx-auto flex justify-center items-center py-0.5 border-[1px] border-borderPrimary rounded-full ">
          <span className="body_text_xs text-textHeadingColor font-inter font-medium">{`${kirtan?.n === null ? 1 : kirtan?.n}/${
            kirtan?.tp || kirtan?.n
          }`}</span>
        </div>
      </div>
    </div>
  );
};

const SkeletonLoader = () => {
  return (
    // <div className="w-full border-y border-y-borderPrimary my-2">
    //   <div className="flex flex-col w-full h-full items-start space-y-2 p-1.5">
    //     <div className="w-full h-5 bg-textSecondaryColor opacity-50 rounded-md"></div>
    //     <div className="w-3/4 h-4 bg-textSecondaryColor opacity-50 rounded-md"></div>
    //   </div>
    // </div>

    <CircularProgress sx={{ color: COLORS.iconOrange }} />
  );
};
export default KirtanLyricsData;
